import sys from '@truckdown/jsclient';
import * as icons from '@truckdown/jsclient/icons';
import * as apis from '@truckdown/jsclient/apis';
import * as locations from '@truckdown/locations';
import { defineComponent, ref, computed, onMounted, onUnmounted, watch, ComputedRef, Ref } from 'vue';
import { IScriptLoader } from '../Services/IScriptLoader';
import { ILocationsUtilities } from '../Services/ILocationsUtilities';

sys.fontLibrary.add(icons.solid.faLocationArrow);
sys.fontLibrary.add(icons.solid.faScrewdriverWrench);

//utilities

let taskCount = 0;
let tasks: Map<number, Promise<void>> = new Map<number, Promise<void>>();

class Mountedloader {
    private promises: Promise<void>[] = [];

    add<T>(func: Promise<void>) {
        this.promises.push(func);
    }

    wait() {
        return Promise.all(this.promises);
    }
}

const loader = new Mountedloader();


const timeOutMS = 500;
const triggerDelayedTask = function <T>(
    dataRequest: () => Promise<T>,
    dataProcess: (data: T) => void) {

    taskCount++;
    let cnt = taskCount;

    var request = new Promise(resolve => setTimeout(resolve, timeOutMS))
        .then(async () => {
            if (tasks.has(cnt)) {
                let data = await dataRequest();

                if (tasks.has(cnt)) {
                    dataProcess(data);
                    tasks.delete(cnt);
                }
            }
        })
        .catch((error) => {
            console.error(error);
            tasks.delete(cnt);
        });

    tasks.clear();
    tasks.set(taskCount, request);
};

const userInfo = computed(() => {
    return {
        isAuth: sys.framework.context.isAuthenticated(),
        isFleet: sys.framework.context.isInRole(apis.framework.SecurityConstants.Roles.FleetUserRole)
    };
});

var findShortName = function (json: google.maps.GeocoderResult, type: string): string | undefined {
    if (json.address_components) {
        var items = json.address_components.filter(item => {
            return item.types.filter(i => i == type).length > 0;
        });

        if (items.length > 0) {
            return items[0].short_name as string;
        }
    }

    return undefined;
}

const asyncGeoLocation = function (): Promise<apis.tools.City> {
    if (!navigator.geolocation) {
        return new Promise<apis.tools.City>((resolve, reject) => {
            reject();
        });
    }

    return new Promise<apis.tools.City>((resolve, reject) => {
        console.log('getting current location');
        navigator.geolocation.getCurrentPosition(async (position) => {
            console.log('geocoding position');
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;

            let utilities = sys.framework.getRequiredService<ILocationsUtilities>('ILocationsUtilities');
            var res = await utilities.asyncReverseGeocode(latitude, longitude);
            resolve(res);
        }, () => {
            reject();
        }, {
            enableHighAccuracy: false,
            timeout: 5000,
            maximumAge: 3600000
        });
    });
}

var setupCaptcha = function (instanceValue: string) {
    const instance = ref<string>(instanceValue);
    const display = ref<boolean>(false);
    const isBlocked = ref<boolean>(false);

    sys.events.on('captcha-success', function (val: string) {
        if (instance.value == val) {
            display.value = false;
        }
    });

    onMounted(async () => {
        var restrictionManager = sys.framework.getRequiredService<apis.tools.IRestrictionManager>("IRestrictionManager");
        var status = await restrictionManager.getStatus(sys.framework.context);

        if (status == apis.tools.ClientRestrictionStatus.Warning) {
            display.value = true;
        }

        if (status == apis.tools.ClientRestrictionStatus.Active) {
            isBlocked.value = true;
        }
    });

    return {
        'captchaDisplay': display,
        'captchaInstance': instance,
        'captchaIsBlocked': isBlocked
    };
}


interface IDropDownState<T> {
    placeholder: string;
    showContent: boolean;
    input: string;
    value?: T;
    result?: apis.tools.IRelationshipResult
}

interface IDropDownSetup {
    openDropDownId: Ref<string>;
    closeDropdown: (ev?: Event) => void;
    openDropdown: (id: string) => void;
    defaultState: <T>(placeholder: string) => IDropDownState<T>;
}

const setupDropDowns = function () {
    const openDropDownId = ref<string>('');

    const closeDropdown = function (ev?: Event) {
        tasks.clear();
        openDropDownId.value = '';
    };

    const openDropdown = function (id: string) {
        tasks.clear();
        openDropDownId.value = id;
    };

    function defaultState<T>(placeholder: string): IDropDownState<T> {
        return {
            placeholder: placeholder,
            showContent: false,
            input: ''
        };
    }

    return {
        openDropDownId,
        closeDropdown,
        openDropdown,
        defaultState
    } as IDropDownSetup;
}

interface ISearchTypeSetup {
    searchType: Ref<string>;
    isSpecialSearch: ComputedRef<boolean>;
    regions: Ref<apis.tools.Region[]>;
    canadianRegions: ComputedRef<apis.tools.Region[]>;
    usRegions: ComputedRef<apis.tools.Region[]>;
}

const setupSearchType = function (request?: locations.SearchRequestBase) {
    const searchType = ref<string>('');
    const isSpecialSearch = computed(() => {
        return searchType.value == 'account'
            || searchType.value == 'name'
            || searchType.value == 'phone';
    });

    const regions = ref<apis.tools.Region[]>([]);

    const canadianRegions = computed(() => {
        return (regions.value as apis.tools.Region[])
            .filter((rgn) => rgn.countryCode === 'CA');
    });

    const usRegions = computed(() => {
        return (regions.value as apis.tools.Region[])
            .filter((rgn) => rgn.countryCode === 'US');
    });

    const setRegions = async function () {
        let sharedEntities = sys.framework.getRequiredService<apis.tools.ISharedEntitiesManager>('ISharedEntitiesManager');
        regions.value = await sharedEntities.allRegions();
    }

    onMounted(async () => {

        let utilities = sys.framework.getRequiredService<ILocationsUtilities>('ILocationsUtilities');
        var promise = utilities.loadRegions;
        loader.add(promise);
        await promise;
        regions.value = utilities.regions;

        if (request && request != null) {
            switch (request.type) {
                case locations.SearchType.Account:
                    searchType.value = 'account';
                    break;
                case locations.SearchType.Phone:
                    searchType.value = 'phone';
                    break;

                default:
                    break;
            }
        }
    });

    return {
        searchType,
        isSpecialSearch,
        regions,
        canadianRegions,
        usRegions
    } as ISearchTypeSetup;
}

interface ILocationSetup {
    locationState: Ref<IDropDownState<string>>,
    locationError: Ref<string>,
    selectedCity: Ref<apis.tools.City | null>,
    cities: Ref<apis.tools.City[]>,
    getCityDisplay: (city: apis.tools.City) => string,
    triggerLocationLookup: (input: string) => void,
    showLocationsHint: ComputedRef<boolean>,
    showLocationInput: ComputedRef<boolean>,
    currentLocationFailed: Ref<boolean>,
    citiesLoaded: Ref<boolean>,
    supportsCurrentLocation: ComputedRef<boolean>,
    useCurrentLocation: () => Promise<void>,
    focusLocation: (ev: Event) => void,
    selectCity: (city: apis.tools.City | null) => void,
    deselectCity: (ev: Event) => void,
    clearLocation: () => void,
    onLocationBlur: (ev: Event) => void,
    currentLocationLoading: ComputedRef<boolean>
}

const setupLocation = function (searchType: ISearchTypeSetup,
    dropDown: IDropDownSetup,
    request?: locations.SearchRequestBase) {

    let locationError = ref<string>("");
    let citiesLoaded = ref<boolean>(false);

    const locationState = ref<IDropDownState<string>>(dropDown.defaultState<string>('Where do you need service?'));

    const selectedCity = ref<apis.tools.City | null>(null);
    const cities = ref<apis.tools.City[]>([]);
    const getCityDisplay = function (city: apis.tools.City) {
        if (!city.id || city.id.length === 0) {
            return city.name;
        } else {
            return city.name + ', ' + city.regionCode;
        }
    }

    const triggerLocationLookup = function (input: string) {
        //citiesLoaded.value = false;
        let type = searchType.searchType.value;
        triggerDelayedTask(async () => {
            let cityManager = sys.framework.getRequiredService<apis.tools.ICityManager>('ICityManager');

            return await cityManager.search(input, {
                rgn: type,
                limit: 15
            } as apis.tools.CitySearchOptions);
        }, (data) => {
            cities.value = data;
            citiesLoaded.value = true;
        });
    };

    const showLocationsHint = computed(() => {
        return (cities.value.length < 1 && !locationState.value.input && !citiesLoaded.value && locationState.value.placeholder === 'Where do you need service?');
    });

    const showLocationInput = computed(() => {
        return searchType.isSpecialSearch.value || selectedCity.value == null;
    });

    const currentLocationLoading = ref<boolean>(false);
    const currentLocationFailed = ref<boolean>(false);

    const supportsCurrentLocation = computed(() => {
        return !currentLocationFailed.value && navigator.geolocation;
    });

    const useCurrentLocation = async function () {
        currentLocationLoading.value = true;

        try {
            var data = await asyncGeoLocation();
            if (data == null) {
                currentLocationFailed.value = true;
            } else {
                selectCity(data);
            }
        } catch (err) {
            console.error('Error getting current location', err);
        } finally {
            currentLocationLoading.value = false;
        }
    }

    const focusLocation = function (ev: Event) {
        dropDown.openDropdown('location');
    }

    const selectCity = function (city: apis.tools.City | null) {
        console.log('selecting city', city);
        if (city) {
            locationError.value = "";
        }
       
        selectedCity.value = city;
        locationState.value.input = '';
        dropDown.closeDropdown();
    }

    const deselectCity = function (ev: Event) {
        selectedCity.value = null;
    }

    const clearLocation = function () {
        tasks.clear();
        selectCity(null);
        cities.value = [];
        citiesLoaded.value = false;
    }

    const onLocationBlur = function (ev: Event) {
        setTimeout(function () {
            if (locationState.value.input && cities.value.length > 0) {
                selectedCity.value = cities.value[0];
                locationState.value.input = '';
                if (dropDown.openDropDownId.value == 'location') {
                    dropDown.closeDropdown();
                }
            } 
        }, 250);
    }

    onMounted(async () => {
        await loader.wait();
        if (request && request != null) {
            switch (request.type) {
                case locations.SearchType.Account:
                    locationState.value.input = request.meta["AccountNumber"];
                    break;

                case locations.SearchType.Area:
                    break;

                case locations.SearchType.Phone:
                    locationState.value.input = request.meta["Phone"];
                    break;
                    
                default:
                    console.log('Selecting city');

                    var city = new apis.tools.City();

                    city.id = request.meta["CityId"];
                    city.countryCode = request.meta["CountryCode"];
                    city.regionCode = request.meta["RegionCode"];
                    city.name = request.meta["City"] ?? request.meta["LocationDisplay"];
                    city.point = {
                        lat: (request.meta["Lat"] ?? 0) as number,
                        lng: (request.meta["Lng"] ?? 0) as number
                    };

                    console.log('Created city', city);

                    cities.value = [city];
                    selectCity(city); 
                    break;
            }
        }
    });

    watch(searchType.searchType, (newVal: string, oldVal: string) => {
        //only clear the location if oldVal has a value or when new regionCode
        //is different from the selected location one.
        //When initially set, oldVal will not have a value
        //this is done because the searchType is set from the request after the selected city is set

        if (oldVal || (selectedCity.value && selectedCity.value.regionCode !== (newVal))) {
            clearLocation();
        }

        locationError.value = "";
        switch (newVal) {
            case 'name':
                locationState.value.placeholder = 'Which business name?';
                break;

            case 'phone':
                locationState.value.placeholder = 'Which phone number?';
                break;

            case 'account':
                locationState.value.placeholder = 'Which account number?';
                break;

            default:
                locationState.value.placeholder = 'Where do you need service?';
                break;
        };
    });

    watch(() => locationState.value.input, (newVal: string, oldVal: string) => {
        if (newVal !== oldVal && !searchType.isSpecialSearch.value) {
            if (newVal.length > 0) {
                triggerLocationLookup(newVal);
                locationError.value = "";
            }
        }
    });

    return {
        locationState,
        selectedCity,
        cities,
        getCityDisplay,
        triggerLocationLookup,
        showLocationsHint,
        showLocationInput,
        useCurrentLocation,
        focusLocation,
        selectCity,
        deselectCity,
        currentLocationFailed,
        supportsCurrentLocation,
        currentLocationLoading,
        locationError,
        onLocationBlur,
        citiesLoaded
    } as ILocationSetup;
}

interface IServiceSetup {
    serviceState: Ref<IDropDownState<string>>,
    servicesError: Ref<string>,
    selectedServices: Ref<apis.tools.Service[]>,
    services: Ref<apis.tools.Service[]>,
    filteredServices: ComputedRef<apis.tools.Service[]>,
    filteredNetworks: ComputedRef<apis.tools.Service[]>,
    isServiceSelected: (code: string) => boolean,
    selectService: (code: string) => void,
    deselectService: (code: string) => void,
    focusService: (ev: Event) => void
}

const setupServices = function (dropDown: IDropDownSetup,
    request?: locations.SearchRequestBase, selectedServiceCode?: string) {

    const serviceState = ref<IDropDownState<string>>(dropDown.defaultState<string>('What service do you need?'));
    let servicesError = ref<string>("");
    const selectedServices = ref<apis.tools.Service[]>([]);
    const services = ref<apis.tools.Service[]>([]);

    const filteredServices = computed(() => {
        var input = serviceState.value.input;
        return services.value
            .filter((itm) => {
                return itm.type === apis.tools.ServiceType.Service
                    && (input.length === 0
                        || itm.tags.some((tg) => {
                            return tg.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        })
                        || itm.name.toLowerCase().indexOf(input.toLowerCase()) >= 0)
                    && !isServiceSelected(itm.code);
            });
    });

    const filteredNetworks = computed(() => {
        var input = serviceState.value.input;
        return services.value
            .filter((itm) => {
                return itm.type === apis.tools.ServiceType.DealerNetwork
                    && (input.length === 0
                        || itm.tags.some((tg) => {
                            return tg.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        })
                        || itm.name.toLowerCase().indexOf(input.toLowerCase()) >= 0)
                    && !isServiceSelected(itm.code);
            });
    });

    const isServiceSelected = function (code: string) {
        return selectedServices.value.some((itm) => {
            return itm.code == code;
        });
    }

    const getSelectedServicesOfType = function (type: apis.tools.ServiceType) {
        return selectedServices.value
            .filter((itm) => {
                return itm.type == type;
            });
    }

    const selectService = function (code: string) {
        if (!isServiceSelected(code)) {
            let selected = services.value
                .filter((itm) => {
                    return itm.code == code;
                })[0];

            if (selected) {
                if (selected.type == apis.tools.ServiceType.DealerNetwork) {
                    //deselect all services
                    selectedServices.value = getSelectedServicesOfType(apis.tools.ServiceType.DealerNetwork);
                }
                else if (selected.type == apis.tools.ServiceType.Service) {
                    //deselect all dealer networks
                    selectedServices.value = getSelectedServicesOfType(apis.tools.ServiceType.Service);
                }

                //allow multile services for ace users only
                if (userInfo.value.isFleet) {
                    selectedServices.value.push(selected);
                } else {
                    selectedServices.value = [selected];
                }

                serviceState.value.input = '';
                //close list after selection
                dropDown.closeDropdown();
                servicesError.value = "";
            };
        }
    };


    const deselectService = function (code: string) {
        let idx = selectedServices.value.findIndex((itm) => {
            return itm.code == code;
        });
        if (idx >= 0) {
            selectedServices.value.splice(idx, 1);
        }
    };

    const focusService = function (ev: Event) {
        dropDown.openDropdown('service');
    }

    const loadServices = async function () {
        let utilities = sys.framework.getRequiredService<ILocationsUtilities>('ILocationsUtilities');
        await utilities.loadServices;
        services.value = utilities.services;
    }

    onMounted(async () => {
        let utilities = sys.framework.getRequiredService<ILocationsUtilities>('ILocationsUtilities');
        var promise = loadServices();
        loader.add(promise);

        await loader.wait();
        if (request && request.services.length > 0) {
            for (let i = 0; i < request.services.length; i++) {
                selectService(request.services[i]);
            }
        } else if ((selectedServiceCode?.length ?? 0) > 0) {
            selectService(selectedServiceCode ?? '');
        } else if (userInfo.value.isFleet) {
            var claims = sys.framework.context.getClaims('search_default_service');
            if (claims.length > 0) {
                for (let ci = 0; ci < claims.length; ci++) {
                    selectService(claims[ci].val);
                }
            }
        }
    });


    watch(() => serviceState.value.input, (newVal: string, oldVal: string) => {
        if (newVal !== oldVal) {
            if (newVal.length > 0) {
                servicesError.value = "";
            }
        }
    });

    return {
        serviceState,
        selectedServices,
        services,
        filteredServices,
        filteredNetworks,
        isServiceSelected,
        selectService,
        deselectService,
        focusService,
        servicesError
    } as IServiceSetup;
}

interface IOptionsSetup {
    optionsState: Ref<IDropDownState<string>>,
    showOptions: Ref<boolean>,
    toggleOptions: (ev: Event) => void,
    showOptionsButton: ComputedRef<boolean>,
    selectedAmenities: Ref<apis.tools.Service[]>,
    amenities: ComputedRef<apis.tools.Service[]>,
    filteredAmenities: ComputedRef<apis.tools.Service[]>,
    isAmenitySelected: (code: string) => boolean,
    selectAmenity: (code: string) => void,
    deselectAmenity: (code: string) => void,
    focusAmentity: (ev: Event) => void,
    searchOnDistance: Ref<boolean>,
    suppressManaged: Ref<boolean>
}

const setupOptions = function (
    dropDown: IDropDownSetup, setupSearchType: ISearchTypeSetup,
    servicesSetup: IServiceSetup, request?: locations.SearchRequestBase) {
       
    const optionsState = ref<IDropDownState<string>>(dropDown.defaultState<string>('Only include locations with:'));
    const showOptions = ref<boolean>(false);

    const searchOnDistance = ref<boolean>(false);
    const suppressManaged = ref<boolean>(false);

    const toggleOptions = function (ev: Event) {
        showOptions.value = !showOptions.value;
    };

    const showOptionsButton = computed(() => {
        return !setupSearchType.isSpecialSearch.value && (userInfo.value.isFleet || amenities.value.length > 0);
    });

    const selectedAmenities = ref<apis.tools.Service[]>([]);

    const amenities = computed(() => {
        return servicesSetup.services.value
            .filter((itm) => {
                return itm.type === apis.tools.ServiceType.Amenity
                    && servicesSetup.selectedServices.value.some(s => s.childServices.indexOf(itm.code) >= 0);
            });
    });

    const filteredAmenities = computed(() => {
        var input = optionsState.value.input;
        return amenities.value
            .filter((itm) => {
                return (input.length === 0
                    || itm.tags.some((tg) => {
                        return tg.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    })
                    || itm.name.toLowerCase().indexOf(input.toLowerCase()) >= 0)
                && !isAmenitySelected(itm.code);
            });
    });

    const isAmenitySelected = function (code: string) {
        return selectedAmenities.value.some((itm) => {
            return itm.code == code;
        });
    }

    const selectAmenity = function (code: string) {
        if (!isAmenitySelected(code)) {
            console.log('selecting amenity', code);
            let selected = servicesSetup.services.value
                .filter((itm) => {
                    return itm.code == code;
                })[0];

            console.log('selecting amenity', selected);
            if (selected) {
                selectedAmenities.value.push(selected);
            };
        }
    };

    const deselectAmenity = function (code: string) {
        let idx = selectedAmenities.value.findIndex((itm) => {
            return itm.code == code;
        });
        if (idx >= 0) {
            selectedAmenities.value.splice(idx, 1);
        }
    };

    const focusAmentity = function (ev: Event) {
        dropDown.openDropdown('amenities');
    }

    onMounted(async () => {
        await loader.wait();
        if (request) {
            if (userInfo.value.isFleet) {
                if (request.options.searchByDistance) {
                    searchOnDistance.value = true;
                }
                if (request.options.suppressManagedServices) {
                    suppressManaged.value = true;
                }
            }

            if (request.filters.length > 0) {
                console.log('selecting amenities', request.filters);
                for (let i = 0; i < request.filters.length; i++) {
                    selectAmenity(request.filters[i]);
                }
            }
        } else {
            if (userInfo.value.isFleet) {

                let sodVal = sys.framework.context.getSetting('search_distance_search');
                let smsVal = sys.framework.context.getSetting('hide_managed_services');

                let sod = sodVal == 'True'
                    || sodVal == 'True'
                    || sodVal == 'True';
                let sms = smsVal == 'True'
                    || smsVal == 'True'
                    || smsVal == 'True';

                if (sod) {
                    searchOnDistance.value = true;
                }
                if (sms) {
                    suppressManaged.value = true;
                }
            }
        }

    });

    return {
        optionsState,
        showOptions,
        toggleOptions,
        showOptionsButton,
        selectedAmenities,
        amenities,
        filteredAmenities,
        isAmenitySelected,
        selectAmenity,
        deselectAmenity,
        focusAmentity,
        searchOnDistance,
        suppressManaged
    } as IOptionsSetup;
}

const setupSearch = function (
    searchTypeSetup: ISearchTypeSetup, locationSetup: ILocationSetup,
  
    servicesSetup: IServiceSetup, optionsSetup: IOptionsSetup) {

    let servicesError = ref<string>("");

    const isLocationNameValid = async function () {
        var service = sys.framework.getRequiredService<apis.tools.IRelationshipService>('IRelationshipService');

        var req = new apis.tools.SearchRelationshipRequest();
        req.type = apis.tools.RelationshipType.Location;
        req.tags = locationSetup.locationState.value.input;

        var res = await service.search(req);

        if (res.cnt > 0) {
            locationSetup.locationState.value.result = res.res[0];
            return true;
        }
        return false;
    }

    const isLocationPhoneValid = function () {
        let val = locationSetup.locationState.value.input;

        val = val.replace(/\D/g, '');
        if (val.length == 10 || val.length == 11) {
            locationSetup.locationState.value.input = val;
            return true;
        }

        return false;
    }

    const isLocationAccountValid = function () {
        let val = locationSetup.locationState.value.input;

        val = val.replace(/\D/g, '');

        if (isNaN(parseFloat(val))) {
            return false;
        }

        locationSetup.locationState.value.input = parseFloat(val) + '';
        return true;
    }

    const isLocationValid = async function () {
        locationSetup.locationError.value = "";
        servicesSetup.servicesError.value = "";
        switch (searchTypeSetup.searchType.value) {
            case 'name':
                if (await isLocationNameValid()) {
                    return true;
                } else {
                    locationSetup.locationError.value = "Location name is not valid"
                    return false;
                }

            case 'phone':
                if (isLocationPhoneValid()) {
                    return true;
                } else {
                    locationSetup.locationError.value = "Location phone number is not valid"
                    return false;
                }

            case 'account':
                if (isLocationAccountValid()) {
                    return true;
                } else {
                    locationSetup.locationError.value = "Account number is not valid"
                    return false;
                }

            default:

                // if location is selected
                if (locationSetup.selectedCity.value != null) {
                    return true;
                // if location is not selected but input is not empty
                } else if (locationSetup.selectedCity.value === null && locationSetup.locationState.value.input) {
                    locationSetup.locationError.value = "Location name is not valid. Please select a location from the dropdown options.";
                    return false;
                // if location is not selected and input is empty
                } else if (locationSetup.selectedCity.value === null && !locationSetup.locationState.value.input) {
                    locationSetup.locationError.value = "Location is required."
                    return false;
                } 
        };
    };

    const isServiceValid = function () {
        if (searchTypeSetup.isSpecialSearch.value) {
            return true;
        }
        else if (servicesSetup.selectedServices.value.length > 0) {
            return true;
        } else if (servicesSetup.selectedServices.value.length == 0 && servicesSetup.serviceState.value.input) {
            servicesSetup.servicesError.value = "Service name is not valid. Please select a name from the dropdown options."
            return false;
        } else {
            servicesSetup.servicesError.value = "Service is required."
            return false;
        }
    };

    const isValid = async function () {
        return await isLocationValid() && isServiceValid();
    };

    const getSelectedServices = function () {
        return servicesSetup.selectedServices.value.map((svc) => svc.code);
    }

    const getSelectedAmentities = function (): string[] {
        if (optionsSetup.amenities.value.length > 0
            && optionsSetup.selectedAmenities.value.length > 0) {

            return optionsSetup.selectedAmenities.value.filter((am) => {
                return optionsSetup.amenities.value.some((val) => {
                    return val.code == am.code;
                });
            }).map((am) => { return am.code; });
        }

        return [];
    }

    const getSearchUrl = function () {
        switch (searchTypeSetup.searchType.value) {
            case 'name':
                var result = locationSetup.locationState.value.result as apis.tools.IRelationshipResult;
                var name = result.disp.toLowerCase().replace(/\s/g, '_').replace(/[^\w_]/g, '');

                return '/search/view-detail/' + encodeURIComponent(name) + '/' + encodeURIComponent(result._id);
            case 'phone':
                return '/search/results/phn/' + encodeURIComponent(locationSetup.locationState.value.input);

            case 'account':
                return '/search/results/act/' + encodeURIComponent(locationSetup.locationState.value.input);

            default:
                let searchByDistance = false;
                let suppressManagedServices = false;
                if (userInfo.value.isFleet) {
                    if (optionsSetup.searchOnDistance.value) {
                        searchByDistance = true;
                    }
                    if (optionsSetup.suppressManaged.value) {
                        suppressManagedServices = true;
                    }
                }

                let model: locations.SearchRequestModel = {
                    id: '',
                    time: new Date(),
                    contextId: '',
                    site: sys.framework.context.sys,
                    type: locations.SearchType.Point,
                    services: getSelectedServices(),
                    filters: getSelectedAmentities(),
                    options: {
                        searchByDistance: searchByDistance,
                        suppressManagedServices: suppressManagedServices,
                        preferredRadius: locations.SearchRadiusPreference.fifteen,
                        privateRadius: locations.SearchRadiusPreference.hundred,
                        measureUnit: locations.MeasureUnit.Imperial,
                        timezoneInfoId: ''
                    },
                    meta: {
                        'lat': locationSetup.selectedCity.value?.point.lat ?? 0,
                        'lng': locationSetup.selectedCity.value?.point.lng ?? 0,
                        'city': locationSetup.selectedCity.value?.name ?? '',
                        'cityId': locationSetup.selectedCity.value?.id ?? '',
                        'countryCode': locationSetup.selectedCity.value?.countryCode ?? '',
                        'regionCode': locationSetup.selectedCity.value?.regionCode ?? ''
                    }
                }

                let utilities = sys.framework.getRequiredService<ILocationsUtilities>('ILocationsUtilities');
                return utilities.generateUrl(model);
        };
    };

    const runSearch = async function (ev: Event) {
        if (await isValid()) {
            var url = getSearchUrl();
            window.location.href = url;
        }
    }

    return {
        runSearch
    };
}

export default defineComponent({
    props: {
        request: Object,
        serviceCode: String
    },
    setup: function (props, ctx) {
        var request = props.request as locations.SearchRequestBase | undefined;

        var dropDownSetup = setupDropDowns();
        var searchTypeSetup = setupSearchType(request);
        var locationSetup = setupLocation(searchTypeSetup, dropDownSetup, request);
        var servicesSetup = setupServices(dropDownSetup, request, props.serviceCode);
        var optionsSetup = setupOptions(dropDownSetup, searchTypeSetup, servicesSetup, request);
        var searchSetup = setupSearch(searchTypeSetup, locationSetup, servicesSetup, optionsSetup);
        const isPlanRV = ref<Boolean>(sys.framework.context.sys == 'planrv' || false);

        return {
            userInfo,
            none: function () { },
            ...dropDownSetup,
            ...searchTypeSetup,
            ...locationSetup,
            ...servicesSetup,
            ...optionsSetup,
            ...searchSetup,
            ...setupCaptcha('search-form'),
            isPlanRV
        };
    }
});
